import React, { useEffect, useState, useCallback } from 'react';
import { customersService, Customer } from '../lib/db/customers';
import { contactsService, Contact } from '../lib/db/contacts';
import { contractsService, Contract as DbContract } from '../lib/db/contracts';
import { projectsService } from '../lib/db/projects';
import { db } from '../lib/cloudsql';
import { toDateInputValue } from '../lib/dateInputValue';
import {
  isMarkupRateUnit,
  normalizeContractServiceRate,
  storedRateToPercent
} from '../utils/quoteItemAmounts';
import { duplicateContactEmailMessage, findDuplicateContact, contactStatusLabel, isContactActive } from '../utils/contactEmail';
import { PlusIcon, PencilIcon, TrashIcon, UsersIcon, UserGroupIcon, DocumentTextIcon, XMarkIcon, LinkIcon, UserMinusIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const Customers: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'customers' | 'contacts' | 'contracts'>('customers');
  
  // 客戶相關狀態
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [formData, setFormData] = useState({
    company: '',
    phone: '',
    address: '',
    tax_id: '',
    owner: '',
  });

  useEffect(() => {
    if (activeTab === 'customers') {
      fetchCustomers();
    }
  }, [activeTab]);

  const fetchCustomers = async () => {
    try {
      const data = await customersService.getAll([{ field: 'created_at', direction: 'desc' }]);
      setCustomers(data);
    } catch (error) {
      console.error('取得客戶列表失敗:', error);
      alert('取得客戶列表失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await customersService.update(editingCustomer.id, formData);
      } else {
        await customersService.create(formData);
      }
      setShowModal(false);
      setEditingCustomer(null);
      resetForm();
      fetchCustomers();
    } catch (error) {
      console.error('儲存客戶失敗:', error);
      alert('儲存客戶失敗，請稍後再試');
    }
  };

  const handleEdit = (customer: Customer) => {
    setEditingCustomer(customer);
    setFormData({
      company: customer.company,
      phone: customer.phone || '',
      address: customer.address || '',
      tax_id: customer.tax_id || '',
      owner: customer.owner || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('確定要刪除此客戶嗎？')) {
      try {
        await customersService.delete(id);
        fetchCustomers();
      } catch (error) {
        console.error('刪除客戶失敗:', error);
        alert('刪除客戶失敗，請稍後再試');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      company: '',
      phone: '',
      address: '',
      tax_id: '',
      owner: '',
    });
  };

  const openAddModal = () => {
    setEditingCustomer(null);
    resetForm();
    setShowModal(true);
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">客戶管理</h1>
        {activeTab === 'customers' && (
          <button
            onClick={openAddModal}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            新增客戶
          </button>
        )}
      </div>

      {/* Tab 導航 */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('customers')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === 'customers'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <UsersIcon className="w-5 h-5 mr-2" />
            客戶
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === 'contacts'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <UserGroupIcon className="w-5 h-5 mr-2" />
            聯絡人
          </button>
          <button
            onClick={() => setActiveTab('contracts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === 'contracts'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <DocumentTextIcon className="w-5 h-5 mr-2" />
            合約
          </button>
        </nav>
      </div>

      {/* Tab 內容 */}
      {activeTab === 'customers' && (
        <div className="space-y-6">

      {/* 客戶列表 */}
      <div className="card">
        {customers.length === 0 && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-800">
                  歡迎使用客戶管理系統！您可以新增客戶來管理業務關係。
                </p>

              </div>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  公司名稱
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  統一編號
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  負責人
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{customer.company}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{customer.tax_id || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{customer.owner || '-'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(customer)}
                      className="text-primary hover:text-primary/80 mr-3"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(customer.id)}
                      className="text-danger hover:text-danger/80"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 新增/編輯客戶模態框 */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingCustomer ? '編輯客戶' : '新增客戶'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    公司名稱 *
                  </label>
                  <input
                    type="text"
                    required
                    className="input-field mt-1"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    電話
                  </label>
                  <input
                    type="tel"
                    className="input-field mt-1"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    地址
                  </label>
                  <textarea
                    className="input-field mt-1"
                    rows={3}
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    統一編號
                  </label>
                  <input
                    type="text"
                    className="input-field mt-1"
                    placeholder="12345678"
                    value={formData.tax_id}
                    onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    負責人
                  </label>
                  <input
                    type="text"
                    className="input-field mt-1"
                    placeholder="請輸入負責人姓名"
                    value={formData.owner}
                    onChange={(e) => setFormData({ ...formData, owner: e.target.value })}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary"
                  >
                    取消
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingCustomer ? '更新' : '新增'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
        </div>
      )}

      {activeTab === 'contacts' && (
        <ContactsTab />
      )}

      {activeTab === 'contracts' && (
        <ContractsTab />
      )}
    </div>
  );
};

// =============================================
// 聯絡人 Tab 組件
// =============================================
interface ContactWithCustomer extends Contact {
  customers?: {
    company: string;
  };
}

const ContactsTab: React.FC = () => {
  const [contacts, setContacts] = useState<ContactWithCustomer[]>([]);
  const [customers, setCustomers] = useState<{ id: string; company: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    name: '',
    email: '',
    phone: '',
    position: '',
  });

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const [contactsData, customersData] = await Promise.all([
        selectedCustomerId
          ? contactsService.getByCustomerId(selectedCustomerId)
          : contactsService.getAll(),
        customersService.getAll([{ field: 'company', direction: 'asc' }]),
      ]);

      const customerMap = new Map(customersData.map((c) => [c.id, c]));
      const contactsWithCustomers: ContactWithCustomer[] = contactsData.map((contact) => ({
        ...contact,
        customers: customerMap.get(contact.customer_id)
          ? { company: customerMap.get(contact.customer_id)!.company }
          : undefined,
      }));

      setContacts(contactsWithCustomers);
      setCustomers(customersData.map((c) => ({ id: c.id, company: c.company })));
    } catch (error) {
      console.error('取得聯絡人列表失敗:', error);
      alert(error instanceof Error ? error.message : '取得聯絡人列表失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [selectedCustomerId]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const duplicate = findDuplicateContact(contacts, formData, editingContact?.id);
    if (duplicate) {
      alert(duplicateContactEmailMessage(duplicate));
      return;
    }
    
    try {
      if (editingContact) {
        await contactsService.update(editingContact.id, formData);
      } else {
        await contactsService.create(formData);
      }

      setShowModal(false);
      setEditingContact(null);
      resetForm();
      fetchContacts();
    } catch (error) {
      console.error('儲存聯絡人失敗:', error);
      alert(error instanceof Error ? error.message : '儲存聯絡人失敗，請稍後再試');
    }
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setFormData({
      customer_id: contact.customer_id,
      name: contact.name,
      email: contact.email || '',
      phone: contact.phone || '',
      position: contact.position || '',
    });
    setShowModal(true);
  };

  const handleSetStatus = async (contact: Contact, status: 'active' | 'inactive') => {
    const confirmed = window.confirm(
      status === 'inactive'
        ? `確定將「${contact.name}」設為離職？將從聯絡人列表隱藏，過去的報價單仍會顯示此人。`
        : `確定將「${contact.name}」恢復為在職？`
    );
    if (!confirmed) return;
    try {
      await contactsService.setStatus(contact.id, status);
      fetchContacts();
    } catch (error) {
      console.error('更新聯絡人狀態失敗:', error);
      alert(error instanceof Error ? error.message : '更新聯絡人狀態失敗，請稍後再試');
    }
  };

  const handleDelete = async (contact: Contact) => {
    if (!window.confirm(`確定要刪除「${contact.name}」？刪除後歷史報價可能無法對到此聯絡人。若只需從列表隱藏，請改用「設為離職」。`)) {
      return;
    }
    try {
      await contactsService.delete(contact.id);
      fetchContacts();
    } catch (error) {
      console.error('刪除聯絡人失敗:', error);
      alert(error instanceof Error ? error.message : '刪除聯絡人失敗，請稍後再試');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      name: '',
      email: '',
      phone: '',
      position: '',
    });
  };

  const openAddModal = () => {
    setEditingContact(null);
    resetForm();
    setShowModal(true);
  };

  const displayedContacts = showInactive
    ? contacts
    : contacts.filter((c) => isContactActive(c));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">聯絡人管理</h2>
          <p className="mt-1 text-sm text-gray-500">
            管理客戶聯絡人；設為離職會從列表隱藏，歷史報價仍可對到原聯絡人
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          新增聯絡人
        </button>
      </div>

      {/* 客戶篩選器 */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">篩選客戶：</label>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
            >
              <option value="">全部客戶</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.company}
                </option>
              ))}
            </select>
            <label className="inline-flex items-center text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <span className="ml-2">顯示離職</span>
            </label>
          </div>
          <div className="text-sm text-gray-500">
            共 {displayedContacts.length} 筆聯絡人
          </div>
        </div>
      </div>

      {/* 聯絡人列表 */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  姓名
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  客戶
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  電子郵件
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayedContacts.map((contact) => (
                <tr key={contact.id} className={isContactActive(contact) ? 'hover:bg-gray-50' : 'hover:bg-gray-50 bg-gray-50'}>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isContactActive(contact) ? 'text-gray-900' : 'text-gray-500'}`}>
                    {contact.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {contact.customers?.company || '未知客戶'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {contact.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={isContactActive(contact) ? 'text-green-700' : 'text-gray-500'}>
                      {contactStatusLabel(contact.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(contact)}
                        className="text-primary hover:text-primary/80"
                        title="編輯"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      {isContactActive(contact) && (
                        <button
                          onClick={() => handleSetStatus(contact, 'inactive')}
                          className="text-warning hover:text-warning/80"
                          title="設為離職"
                        >
                          <UserMinusIcon className="h-4 w-4" />
                        </button>
                      )}
                      {!isContactActive(contact) && (
                        <button
                          onClick={() => handleSetStatus(contact, 'active')}
                          className="text-primary hover:text-primary/80"
                          title="恢復在職"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(contact)}
                        className="text-danger hover:text-danger/80"
                        title="刪除"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 新增/編輯聯絡人 Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingContact ? '編輯聯絡人' : '新增聯絡人'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    客戶
                  </label>
                  <select
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                  >
                    <option value="">選擇客戶</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.company}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    姓名
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    電子郵件
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    電話
                  </label>
                  <input
                    type="tel"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    職位
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary"
                  >
                    取消
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingContact ? '更新' : '新增'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================
// 合約 Tab 組件
// =============================================
interface Contract {
  id?: string;
  project_ids?: string[];
  customer_id: string;
  contract_number: string;
  title: string;
  start_date?: string;
  end_date?: string;
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  payment_method?: 'prepaid' | 'monthly' | string;
  contract_contact_ids?: string[];
  contract_link?: string;
  prepaid_percentage?: number;
  monthly_payment_type?: 'days_15' | 'days_30' | 'next_n_months_day';
  monthly_payment_day?: number;
  monthly_payment_months?: number;
  created_at?: string;
  updated_at?: string;
}

interface ContractServiceItem {
  id?: string;
  contract_id: string;
  service_type: string;
  contract_price: number;
  unit?: string;
  name?: string;
  quantity?: number;
  created_at?: string;
  updated_at?: string;
}

interface ContractCustomer {
  id: string;
  company: string;
  owner?: string;
}

interface ContractProject {
  id: string;
  name: string;
  contract_id?: string;
  customer_id: string;
  status: string; // 使用 string 以支援所有擴展的狀態
}

interface ServiceRate {
  id: string;
  service_type: string;
  hourly_rate?: number;
  unit_price?: number;
  unit?: string;
  year?: number;
  description?: string;
  is_active?: boolean;
  charge_type?: 'fixed' | 'percentage';
}

const ContractsTab: React.FC = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [customers, setCustomers] = useState<ContractCustomer[]>([]);
  const [projects, setProjects] = useState<ContractProject[]>([]);
  const [serviceTypes, setServiceTypes] = useState<string[]>([]);
  const [serviceRates, setServiceRates] = useState<ServiceRate[]>([]);
  const [contractServiceItems, setContractServiceItems] = useState<ContractServiceItem[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [currentContract, setCurrentContract] = useState<Contract | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedCustomerFilter, setSelectedCustomerFilter] = useState<string>('');
  // 防止使用者連點儲存按鈕造成重複請求與 contract_service_items 競態 404
  const [isSavingContract, setIsSavingContract] = useState(false);

  const fetchCustomers = useCallback(async () => {
    try {
      const data = await customersService.getAll([{ field: 'company', direction: 'asc' }]);
      setCustomers(data.map(c => ({ id: c.id, company: c.company, owner: c.owner })));
    } catch (error) {
      console.error('取得客戶列表失敗:', error);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const data = await projectsService.getAll([{ field: 'name', direction: 'asc' }]);
      setProjects(data.map(p => ({
        id: p.id,
        name: p.name,
        contract_id: p.contract_id,
        customer_id: p.customer_id,
        status: p.status || 'active'
      })));
    } catch (error) {
      console.error('取得專案列表失敗:', error);
    }
  }, []);

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await contractsService.getAll([{ field: 'created_at', direction: 'desc' }]);
      
      let filteredData = data;
      if (selectedCustomerFilter) {
        filteredData = filteredData.filter(c => c.customer_id === selectedCustomerFilter);
      }
      
      setContracts(filteredData.map(c => ({
        ...c,
        contract_number: (c as { contract_number?: string }).contract_number || c.id,
        contract_link: (c as { contract_file_link?: string }).contract_file_link || '',
        start_date: toDateInputValue(c.start_date) || '',
        end_date: toDateInputValue((c as { end_date?: string }).end_date) || '',
        status: 'active' as 'draft' | 'active' | 'completed' | 'cancelled',
        payment_method: (c.payment_method as 'prepaid' | 'monthly') || undefined,
        monthly_payment_type: (c.monthly_payment_type as 'days_15' | 'days_30' | 'next_n_months_day') || undefined
      })));
    } catch (error) {
      console.error('取得合約列表失敗:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCustomerFilter]);

  useEffect(() => {
    fetchContracts();
    fetchCustomers();
    fetchProjects();
    fetchServiceTypes();
    fetchServiceRates();
  }, [fetchContracts, fetchCustomers, fetchProjects]);

  const fetchServiceTypes = async () => {
    try {
      const data = await db.query<{ service_type?: string }>('service_rates', {
        select: ['service_type'],
        orderBy: [{ field: 'service_type', direction: 'asc' }]
      });
      
      const types = Array.from(new Set(
        (data || [])
          .map((item) => item.service_type)
          .filter((type): type is string => Boolean(type))
      ));
      
      if (types.length === 0) {
        const defaultTypes = [
          '顧問諮詢',
          '需求訪談與規格文件',
          '前端程式開發',
          '後端程式開發',
          '整合',
          '其他服務'
        ];
        setServiceTypes(defaultTypes);
      } else {
        setServiceTypes(types);
      }
    } catch (error) {
      console.error('取得服務類型失敗:', error);
      const defaultTypes = [
        '顧問諮詢',
        '需求訪談與規格文件',
        '前端程式開發',
        '後端程式開發',
        '整合',
        '其他服務'
      ];
      setServiceTypes(defaultTypes);
    }
  };

  const fetchServiceRates = async () => {
    try {
      const data = await db.query<ServiceRate>('service_rates', {
        orderBy: [{ field: 'service_type', direction: 'asc' }]
      });
      const mappedData = data.map(rate => {
        let unit = '小時';
        let year = new Date().getFullYear();
        if (rate.description) {
          const unitMatch = rate.description.match(/單位:\s*([^|]+)/);
          if (unitMatch) {
            unit = unitMatch[1].trim();
          }
          const yearMatch = rate.description.match(/年份:\s*(\d+)/);
          if (yearMatch) {
            year = parseInt(yearMatch[1]);
          }
        }
        return {
          ...rate,
          unit_price: rate.hourly_rate || rate.unit_price || 0,
          unit: rate.charge_type === 'percentage' ? '%' : (rate.unit || unit),
          year: rate.year || year,
          charge_type: rate.charge_type || 'fixed'
        };
      });
      setServiceRates(mappedData);
    } catch (error) {
      console.error('取得服務費率失敗:', error);
    }
  };

  const fetchContractServiceItems = async (contractId: string) => {
    try {
      const data = await contractsService.getServiceItems(contractId);
      setContractServiceItems(data.map(item => {
        const normalized = normalizeContractServiceRate(item.contract_price, item.unit || '小時');
        return {
          ...item,
          contract_price: normalized.price,
          unit: normalized.unit
        };
      }));
    } catch (error) {
      console.error('取得合約服務項目失敗:', error);
      setContractServiceItems([]);
    }
  };

  const generateContractNumber = async () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const baseNumber = `CT${year}${month}${day}`;
    const allContracts = await contractsService.getAll();
    const existingContracts = allContracts
      .filter(c => c.id && c.id.startsWith(baseNumber))
      .map(c => ({ contract_number: c.id }));
    
    if (!existingContracts || existingContracts.length === 0) {
      return `${baseNumber}001`;
    }
    
    const todayNumbers = existingContracts
      .map(contract => {
        const match = contract.contract_number.match(new RegExp(`^${baseNumber}(\\d{3})$`));
        return match ? parseInt(match[1]) : 0;
      })
      .filter(num => num > 0);
    
    const maxNumber = Math.max(...todayNumbers, 0);
    const nextNumber = maxNumber + 1;
    
    return `${baseNumber}${nextNumber.toString().padStart(3, '0')}`;
  };

  const createNewContract = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const contractNumber = await generateContractNumber();
    const newContract: Contract = {
      customer_id: customers.length > 0 ? customers[0].id : '',
      contract_number: contractNumber,
      title: '',
      contract_link: '',
      status: 'draft',
      payment_method: 'monthly',
      monthly_payment_type: 'days_30',
      monthly_payment_months: undefined,
      monthly_payment_day: undefined,
      prepaid_percentage: undefined,
      start_date: today,
      end_date: undefined,
    };
    setCurrentContract(newContract);
    setIsEditing(false);
    setContractServiceItems([]);
    setShowCreateModal(true);
  };

  const editContract = async (contract: Contract) => {
    setCurrentContract(contract);
    setIsEditing(true);
    setShowCreateModal(true);
    
    if (contract.id) {
      await fetchContractServiceItems(contract.id);
    }
  };

  const saveContract = async () => {
    if (!currentContract) return;
    if (isSavingContract) return;

    if (!currentContract.customer_id) {
      alert('請選擇客戶');
      return;
    }
    if (!currentContract.title.trim()) {
      alert('請輸入合約標題');
      return;
    }

    setIsSavingContract(true);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { contract_link, status, updated_at, created_at, ...contractWithoutNumber } = currentContract;
    const cleanedContract = {
      ...contractWithoutNumber,
      contract_number: currentContract.contract_number || undefined,
      contract_file_link: contract_link && contract_link.trim() !== '' ? contract_link.trim() : undefined,
      start_date: currentContract.start_date && currentContract.start_date.trim() !== '' ? currentContract.start_date : undefined,
      end_date: currentContract.end_date && currentContract.end_date.trim() !== '' ? currentContract.end_date : undefined,
      prepaid_percentage: currentContract.prepaid_percentage && !isNaN(currentContract.prepaid_percentage) 
        ? currentContract.prepaid_percentage : undefined,
      monthly_payment_months: currentContract.monthly_payment_months && !isNaN(currentContract.monthly_payment_months) 
        ? currentContract.monthly_payment_months : undefined,
      monthly_payment_day: currentContract.monthly_payment_day && !isNaN(currentContract.monthly_payment_day) 
        ? currentContract.monthly_payment_day : undefined,
      monthly_payment_type: currentContract.payment_method === 'prepaid' ? undefined : (currentContract.monthly_payment_type || 'days_30'),
    };

    try {
      let savedContractId: string;

      if (isEditing) {
        await contractsService.update(currentContract.id!, cleanedContract as unknown as DbContract);
        savedContractId = currentContract.id!;
      } else {
        const newContract = await contractsService.create(cleanedContract as unknown as Omit<DbContract, 'id' | 'created_at' | 'updated_at'>);
        savedContractId = newContract.id;
      }

      // 以服務類型 (service_type) 為對齊鍵，做 diff 更新而非「全刪再全建」，
      // 避免重複呼叫 DELETE 造成的 404 競態，並降低 API 呼叫次數。
      const desiredItems = contractServiceItems
        .filter(item => item.service_type && item.service_type.trim() !== '')
        .map(item => {
          const normalized = normalizeContractServiceRate(
            item.contract_price && !isNaN(item.contract_price) ? item.contract_price : 0,
            item.unit || '小時'
          );
          return {
            service_type: item.service_type.trim(),
            contract_price: normalized.price,
            unit: normalized.unit
          };
        });

      const existingItems = await contractsService.getServiceItems(savedContractId);
      const existingByType = new Map<string, typeof existingItems[number]>();
      for (const item of existingItems) {
        if (item.service_type) {
          existingByType.set(item.service_type, item);
        }
      }
      const desiredTypes = new Set(desiredItems.map(i => i.service_type));

      for (const item of existingItems) {
        if (!item.service_type || !desiredTypes.has(item.service_type)) {
          await contractsService.deleteServiceItem(item.id);
        }
      }

      for (const item of desiredItems) {
        const existing = existingByType.get(item.service_type);
        if (!existing) {
          await contractsService.createServiceItem({
            contract_id: savedContractId,
            service_type: item.service_type,
            contract_price: item.contract_price,
            unit: item.unit,
          } as Omit<ContractServiceItem, 'id' | 'created_at' | 'updated_at'>);
          continue;
        }
        const isSame =
          Number(existing.contract_price) === Number(item.contract_price) &&
          (existing.unit || '小時') === item.unit;
        if (!isSame) {
          await contractsService.updateServiceItem(existing.id, {
            contract_price: item.contract_price,
            unit: item.unit,
          });
        }
      }

      setShowCreateModal(false);
      setCurrentContract(null);
      setIsEditing(false);
      setContractServiceItems([]);
      fetchContracts();
      alert('合約儲存成功！');
    } catch (error) {
      let errorMessage = '儲存失敗，請檢查資料是否正確';
      
      if (error instanceof Error) {
        if (error.message.includes('duplicate key')) {
          errorMessage = '合約編號已存在，請使用不同的編號';
        } else if (error.message.includes('foreign key')) {
          errorMessage = '選擇的客戶不存在，請重新選擇';
        } else if (error.message.includes('not null')) {
          errorMessage = '必填欄位不能為空，請檢查所有必填欄位';
        } else {
          errorMessage = `儲存失敗：${error.message}`;
        }
      } else if (typeof error === 'object' && error !== null) {
        const errorObj = error as Record<string, unknown>;
        if (errorObj.message) {
          errorMessage = `儲存失敗：${errorObj.message}`;
        } else if (errorObj.details) {
          errorMessage = `儲存失敗：${errorObj.details}`;
        }
      }
      
      alert(errorMessage);
    } finally {
      setIsSavingContract(false);
    }
  };

  const addServiceItem = () => {
    if (!currentContract) return;
    
    const newItem: ContractServiceItem = {
      contract_id: currentContract.id || '',
      service_type: '',
      contract_price: 0,
      unit: '小時'
    };
    
    setContractServiceItems([...contractServiceItems, newItem]);
  };

  const importServiceRates = () => {
    if (!currentContract) return;
    
    const filteredRates = serviceRates.filter(rate => rate.is_active !== false);
    
    if (filteredRates.length === 0) {
      alert('沒有可用的服務收費表資料，請先在「服務費率」頁面添加服務類型');
      return;
    }
    
    const newItems: ContractServiceItem[] = filteredRates.map(rate => {
      const normalized = normalizeContractServiceRate(
        rate.unit_price || rate.hourly_rate || 0,
        rate.unit || '小時'
      );
      return {
        contract_id: currentContract.id || '',
        service_type: rate.service_type,
        contract_price: normalized.price,
        unit: normalized.unit
      };
    });
    
    setContractServiceItems(newItems);
  };

  const updateServiceItem = (index: number, field: keyof ContractServiceItem, value: string | number) => {
    const updatedItems = [...contractServiceItems];
    if (field === 'service_type') {
      const selectedRate = serviceRates.find(rate => 
        rate.service_type === value && rate.is_active === true
      ) || serviceRates.find(rate => 
        rate.service_type === value
      );
      
      const rawPrice = selectedRate ? (selectedRate.unit_price || selectedRate.hourly_rate || 0) : 0;
      const rawUnit = selectedRate ? (selectedRate.unit || '小時') : '小時';
      const normalized = normalizeContractServiceRate(rawPrice, rawUnit);
      updatedItems[index] = {
        ...updatedItems[index],
        service_type: value as string,
        unit: normalized.unit,
        contract_price: normalized.price,
      };
    } else if (field === 'contract_price') {
      const numValue = typeof value === 'string' ? parseFloat(value) : value;
      updatedItems[index] = { ...updatedItems[index], [field]: isNaN(numValue) ? 0 : numValue };
    } else {
      updatedItems[index] = { ...updatedItems[index], [field]: value as string };
    }
    setContractServiceItems(updatedItems);
  };

  const removeServiceItem = (index: number) => {
    const updatedItems = contractServiceItems.filter((_, i) => i !== index);
    setContractServiceItems(updatedItems);
  };

  const deleteContract = async (contractId: string) => {
    if (!confirm('確定要刪除此合約嗎？')) return;
    
    try {
      await contractsService.delete(contractId);
      fetchContracts();
    } catch (error) {
      console.error('刪除合約失敗:', error);
      alert('刪除合約失敗，請稍後再試');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft': return '草稿';
      case 'active': return '生效中';
      case 'completed': return '已完成';
      case 'cancelled': return '已取消';
      default: return '未知';
    }
  };

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.company : '未知客戶';
  };

  const getRelatedProjects = (contractId: string) => {
    const relatedProjects = projects.filter(p => p.contract_id === contractId);
    return relatedProjects;
  };

  const openContractLink = (link: string) => {
    if (link) {
      window.open(link, '_blank');
    }
  };

  const formatPaymentTerms = (contract: Contract): string => {
    if (!contract.payment_method) return '未設定';
    
    switch (contract.payment_method) {
      case 'prepaid':
        return `預付：${contract.prepaid_percentage || 0}%`;
      case 'monthly':
        switch (contract.monthly_payment_type) {
          case 'days_15':
            return '月結：發票後 15 天';
          case 'days_30':
            return '月結：發票後 30 天';
          case 'next_n_months_day':
            return `月結：次 ${contract.monthly_payment_months || 1} 個月 ${contract.monthly_payment_day || 1} 日`;
          default:
            return '月結：未指定';
        }
      default:
        return '未指定';
    }
  };

  const openStatusModal = (contract: Contract) => {
    setCurrentContract(contract);
    setShowStatusModal(true);
  };

  const updateContractStatus = async (newStatus: 'draft' | 'active' | 'completed' | 'cancelled') => {
    if (!currentContract || !currentContract.id) return;
    
    try {
      setCurrentContract({ ...currentContract, status: newStatus });
      setShowStatusModal(false);
      setCurrentContract(null);
      fetchContracts();
      alert('狀態已更新（僅前端顯示，未保存至資料庫）');
    } catch (error) {
      console.error('更新狀態失敗:', error);
      alert('更新狀態失敗，請稍後再試');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">客戶合約管理</h2>
          <p className="text-gray-600">管理客戶服務合約</p>
        </div>
        <button
          onClick={() => createNewContract()}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          新增合約
        </button>
      </div>

      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">篩選客戶：</label>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              value={selectedCustomerFilter}
              onChange={(e) => setSelectedCustomerFilter(e.target.value)}
            >
              <option value="">全部客戶</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.company}
                </option>
              ))}
            </select>
          </div>
          <div className="text-sm text-gray-500">
            共 {contracts.length} 筆合約
          </div>
        </div>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <h3 className="text-lg font-medium text-gray-900 mb-4">合約列表</h3>
          {contracts.length === 0 ? (
            <div className="text-center py-8">
              <DocumentTextIcon className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500">尚未建立任何合約</p>
              <p className="text-sm text-gray-400 mt-1">點擊「新增合約」開始建立</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      合約資訊
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      關聯專案
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      付款條件
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      合約連結
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      狀態
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contracts.map((contract) => (
                    <tr key={contract.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="space-y-1">
                          <div className="font-medium text-gray-900">
                            {contract.contract_number}
                          </div>
                          <div className="text-gray-500">
                            {contract.title}
                          </div>
                          <div className="text-xs text-gray-400">
                            {getCustomerName(contract.customer_id)}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          const relatedProjects = getRelatedProjects(contract.id!);
                          if (relatedProjects.length === 0) {
                            return <span className="text-gray-400">無關聯專案</span>;
                          }
                          return (
                            <div className="space-y-1">
                              {relatedProjects.map((project) => (
                                <div key={project.id} className="flex items-center space-x-2">
                                  <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                    {project.name}
                                  </span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatPaymentTerms(contract)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {contract.contract_link ? (
                          <button
                            onClick={() => openContractLink(contract.contract_link!)}
                            className="inline-flex items-center text-blue-600 hover:text-blue-900"
                            title="開啟合約連結"
                          >
                            <LinkIcon className="h-4 w-4 mr-1" />
                            查看合約
                          </button>
                        ) : (
                          <span className="text-gray-400">未設定</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contract.status || 'active')} cursor-pointer hover:opacity-80`}
                          onClick={() => openStatusModal(contract)}
                          title="點擊變更狀態"
                        >
                          {getStatusText(contract.status || 'active')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => editContract(contract)}
                          className="text-primary hover:text-primary-dark mr-3"
                          title="編輯"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteContract(contract.id!)}
                          className="text-red-600 hover:text-red-900"
                          title="刪除"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 新增/編輯合約模態框 - 由於內容很長，這裡只顯示關鍵部分 */}
      {showCreateModal && currentContract && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40" />
          <div className="relative z-50 w-full max-w-4xl bg-white rounded-md shadow-lg border p-6 max-h-screen overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                {isEditing ? '編輯合約' : '新增合約'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCurrentContract(null);
                  setIsEditing(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-8">
              {/* 基本資訊 */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-4">基本資訊</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">合約編號 *</label>
                    <input
                      type="text"
                      value={currentContract.contract_number}
                      onChange={(e) => setCurrentContract({...currentContract, contract_number: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">合約標題 *</label>
                    <input
                      type="text"
                      value={currentContract.title}
                      onChange={(e) => setCurrentContract({...currentContract, title: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">客戶 *</label>
                    <select
                      value={currentContract.customer_id}
                      onChange={(e) => setCurrentContract({...currentContract, customer_id: e.target.value})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                      required
                    >
                      <option value="">選擇客戶</option>
                      {customers.map((customer) => (
                        <option key={customer.id} value={customer.id}>
                          {customer.company}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">狀態</label>
                    <select
                      value={currentContract.status}
                      onChange={(e) => setCurrentContract({...currentContract, status: e.target.value as 'draft' | 'active' | 'completed' | 'cancelled'})}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    >
                      <option value="draft">草稿</option>
                      <option value="active">生效中</option>
                      <option value="completed">已完成</option>
                      <option value="cancelled">已取消</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* 合約期間 */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-green-900 mb-4">合約期間</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">合約開始日期 *</label>
                    <input
                      type="date"
                      required
                      value={currentContract.start_date || ''}
                      onChange={e => setCurrentContract({ ...currentContract, start_date: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">合約結束日期</label>
                    <input
                      type="date"
                      value={currentContract.end_date || ''}
                      onChange={e => setCurrentContract({ ...currentContract, end_date: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>
              </div>

              {/* 合約連結 */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-purple-900 mb-4">合約檔案</h4>
                <div>
                  <label className="block text-sm font-medium text-gray-700">合約連結位置</label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      <LinkIcon className="h-4 w-4" />
                    </span>
                    <input
                      type="url"
                      value={currentContract.contract_link || ''}
                      onChange={(e) => setCurrentContract({...currentContract, contract_link: e.target.value})}
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border border-gray-300 focus:ring-primary focus:border-primary"
                      placeholder="https://example.com/contract.pdf"
                    />
                  </div>
                </div>
              </div>

              {/* 付款條件 - 簡化版本 */}
              <div className="bg-orange-50 p-4 rounded-lg">
                <h4 className="text-sm font-medium text-orange-900 mb-4">付款條件 *</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="flex items-center mb-3">
                      <input
                        type="radio"
                        name="payment_method"
                        value="prepaid"
                        checked={currentContract.payment_method === 'prepaid'}
                        onChange={(e) => setCurrentContract({
                          ...currentContract, 
                          payment_method: e.target.value as 'prepaid' | 'monthly',
                          monthly_payment_type: undefined,
                        })}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">預付</label>
                    </div>
                    {currentContract.payment_method === 'prepaid' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">預付百分比</label>
                        <div className="flex items-center">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={currentContract.prepaid_percentage || ''}
                            onChange={(e) => setCurrentContract({
                              ...currentContract, 
                              prepaid_percentage: e.target.value ? parseInt(e.target.value) : undefined
                            })}
                            className="block w-20 border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                          />
                          <span className="ml-2 text-sm text-gray-500">%</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="bg-white p-4 rounded-lg border">
                    <div className="flex items-center mb-3">
                      <input
                        type="radio"
                        name="payment_method"
                        value="monthly"
                        checked={currentContract.payment_method === 'monthly'}
                        onChange={(e) => setCurrentContract({
                          ...currentContract, 
                          payment_method: e.target.value as 'prepaid' | 'monthly',
                          prepaid_percentage: undefined
                        })}
                        className="mr-2"
                      />
                      <label className="text-sm font-medium text-gray-700">月結</label>
                    </div>
                    {currentContract.payment_method === 'monthly' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">月結類型</label>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="monthly_payment_type"
                              value="days_15"
                              checked={currentContract.monthly_payment_type === 'days_15'}
                              onChange={(e) => setCurrentContract({
                                ...currentContract, 
                                monthly_payment_type: e.target.value as 'days_15' | 'days_30' | 'next_n_months_day',
                              })}
                              className="mr-2"
                            />
                            <span className="text-sm">發票後 15 天</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="monthly_payment_type"
                              value="days_30"
                              checked={currentContract.monthly_payment_type === 'days_30'}
                              onChange={(e) => setCurrentContract({
                                ...currentContract, 
                                monthly_payment_type: e.target.value as 'days_15' | 'days_30' | 'next_n_months_day',
                              })}
                              className="mr-2"
                            />
                            <span className="text-sm">發票後 30 天</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 收費項目 */}
              <div className="bg-indigo-50 p-4 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-sm font-medium text-indigo-900">收費項目</h4>
                  <div className="space-x-2">
                    <button
                      onClick={importServiceRates}
                      className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md shadow-sm text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      導入服務收費表
                    </button>
                    <button
                      onClick={addServiceItem}
                      className="inline-flex items-center px-3 py-1 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-primary hover:bg-primary-dark"
                    >
                      <PlusIcon className="h-3 w-3 mr-1" />
                      新增項目
                    </button>
                  </div>
                </div>
                <div className="mt-4">
                  {contractServiceItems.length === 0 ? (
                    <p className="text-gray-500">尚未新增收費項目</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">服務類型</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">合約單價／倍率</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">單位</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {contractServiceItems.map((item, index) => (
                            <tr key={item.id || index}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <select
                                  value={item.service_type}
                                  onChange={(e) => updateServiceItem(index, 'service_type', e.target.value)}
                                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                                >
                                  <option value="">選擇服務類型</option>
                                  {serviceTypes.map((type) => (
                                    <option key={type} value={type}>
                                      {type}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <div className="flex items-center space-x-1">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={item.contract_price || ''}
                                    onChange={(e) => updateServiceItem(index, 'contract_price', e.target.value ? parseFloat(e.target.value) : 0)}
                                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
                                    placeholder={isMarkupRateUnit(item.unit) ? '50、100、110' : undefined}
                                  />
                                  {isMarkupRateUnit(item.unit) && (
                                    <span className="text-xs text-gray-500 whitespace-nowrap">%</span>
                                  )}
                                </div>
                                {isMarkupRateUnit(item.unit) && (
                                  <p className="mt-1 text-xs text-gray-400">
                                    {storedRateToPercent(item.contract_price, item.unit)}%：50 收一半、100 實報實銷、110 含 10% 服務費
                                  </p>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  {item.unit}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                  onClick={() => removeServiceItem(index)}
                                  className="text-red-600 hover:text-red-900"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCurrentContract(null);
                    setIsEditing(false);
                  }}
                  disabled={isSavingContract}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  取消
                </button>
                <button
                  onClick={saveContract}
                  disabled={isSavingContract}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSavingContract ? '儲存中…' : '儲存'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 狀態更新模態框 */}
      {showStatusModal && currentContract && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-40" />
          <div className="relative z-50 w-full max-w-md bg-white rounded-md shadow-lg border p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium text-gray-900">更新合約狀態</h3>
              <button
                onClick={() => {
                  setShowStatusModal(false);
                  setCurrentContract(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  合約：<span className="font-medium">{currentContract.contract_number} - {currentContract.title}</span>
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  目前狀態：<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(currentContract.status || 'active')}`}>
                    {getStatusText(currentContract.status || 'active')}
                  </span>
                </p>
              </div>

              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700 mb-3">選擇新狀態：</label>
                {['draft', 'active', 'completed', 'cancelled'].map((status) => (
                  <label key={status} className="flex items-center">
                    <input
                      type="radio"
                      name="status"
                      value={status}
                      defaultChecked={currentContract.status === status}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <span className="ml-3 text-sm text-gray-700">{getStatusText(status)}</span>
                  </label>
                ))}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setCurrentContract(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={() => {
                    const selectedStatusElement = document.querySelector('input[name="status"]:checked') as HTMLInputElement;
                    const selectedStatus = selectedStatusElement?.value as 'draft' | 'active' | 'completed' | 'cancelled';
                    
                    if (!selectedStatus) {
                      alert('請選擇狀態');
                      return;
                    }
                    
                    updateContractStatus(selectedStatus);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary border border-transparent rounded-md hover:bg-primary-dark"
                >
                  確認更新
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers; 